import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { isV2 } from "../lib/config";

export interface DirKid { id: string; guardian_id: string; name: string; birth_date: string | null; rank: string | null; stamps: number; allergies: string | null; photo_consent: boolean }
export interface DirGuardian {
  id: string; full_name: string; phone: string; email: string | null; national_id: string | null; locale: "ar" | "en"; whatsapp_opt_in: boolean; notes: string | null; credit_limit: number; created_at: string;
  visits: number; last_visit: string | null; spent: number; outstandingCredit: number; kids: DirKid[];
}
export interface Directory { total: number; page: number; pageSize: number; guardians: DirGuardian[] }
export type DirSort = "recent" | "name" | "visits" | "spent" | "new";
export const dirKey = ["customers", "directory"] as const;

export const useDirectory = (q: string, page: number, sort: DirSort, pageSize = 25) =>
  useQuery({
    queryKey: [...dirKey, q, page, sort, pageSize], enabled: isV2(), placeholderData: keepPreviousData,
    queryFn: () => api.get<Directory>(`/customers/directory?q=${encodeURIComponent(q)}&page=${page}&pageSize=${pageSize}&sort=${sort}`),
  });

export interface Visit { id: string; kid_name: string; entered_at: string; exited_at: string | null; actual_seconds: number | null; billing_mode: string; status: string; tag_id: string; billed: number | null; receipt_no: string | null }
export const useFamilyHistory = (id: string | null) =>
  useQuery({ queryKey: ["customers", "history", id], enabled: !!id, queryFn: async () => (await api.get<{ visits: Visit[] }>(`/customers/guardians/${id}/history`)).visits });

/** Every family matching the search, fetched page by page, as CSV text (for Excel). */
export async function exportCsv(q: string, sort: DirSort, labels: string[]): Promise<string> {
  const rows: string[][] = [labels];
  for (let page = 1; page <= 50; page++) {
    const r = await api.get<Directory>(`/customers/directory?q=${encodeURIComponent(q)}&page=${page}&pageSize=200&sort=${sort}`);
    for (const g of r.guardians) {
      rows.push([g.full_name, g.phone, g.email ?? "", g.national_id ?? "", g.locale, g.whatsapp_opt_in ? "yes" : "no", g.kids.map((k) => `${k.name}${k.birth_date ? ` (${k.birth_date})` : ""}`).join(" | "), String(g.visits), g.last_visit?.slice(0, 10) ?? "", (g.spent / 1000).toFixed(3), (g.outstandingCredit / 1000).toFixed(3), g.notes ?? ""]);
    }
    if (page * 200 >= r.total) break;
  }
  const cell = (v: string) => `"${v.replace(/"/g, '""').replace(/^([=+\-@])/, "'$1")}"`; // quotes escaped, formulas neutralised
  return "﻿" + rows.map((r) => r.map(cell).join(",")).join("\r\n");
}
